<template>
    <div ref="chatContent" class="chat-content">
        <div v-for="interaction in chat.getInteractions().value" :key="interaction.id">
            <component
                :is="getComponent(interaction)"
                :interaction="interaction"
            />
        </div>

        <generating-response-message v-if="chat.loadingAssistentMessage.value" />
    </div>
</template>

<script setup lang="ts">
    import { nextTick, ref, watch } from 'vue';
    import PreviewMessage from '@/components/messages/PreviewMessage.vue';
    import AssistentMessage from '@/components/messages/AssistentMessage.vue';
    import UserMessage from '@/components/messages/UserMessage.vue';
    import CloseChatMessage from '@/components/messages/CloseChatMessage.vue';
    import EmployeeMessage from '@/components/messages/EmployeeMessage.vue';
    import Chat from '@/chat/Chat';
    import type Interaction from '@/chat/types/Interaction';
    import GeneratingResponseMessage from '@/components/messages/GeneratingResponseMessage.vue';
    import HandoverAcceptedMessage from '@/components/messages/HandoverAcceptedMessage.vue';
    import { InteractionOrigin, InteractionType } from '@keyvue/one-api-client';

    const chat = Chat.getInstance();
    const chatContent = ref<HTMLElement>();

    watch(() => [
        chat.getInteractions().value,
        chat.loadingAssistentMessage.value,
    ], () => {
        nextTick(() => chatContent.value?.scrollTo({
            top: chatContent.value?.scrollHeight,
            behavior: 'smooth',
        }));
    }, {
        deep: true,
        immediate: true,
    });

    function getComponent(interaction: Interaction): any {
        if (interaction.type === 'chat-closed') {
            return CloseChatMessage;
        }

        if (interaction.type === 'preview') {
            return PreviewMessage;
        }

        if (interaction.type === InteractionType.MESSAGE.value) {
            if (interaction.origin === InteractionOrigin.USER.value) {
                return UserMessage;
            }

            if (interaction.origin === InteractionOrigin.ASSISTENT.value) {
                return AssistentMessage;
            }

            if (interaction.origin === 'employee') {
                return EmployeeMessage;
            }
        }

        if (interaction.type === InteractionType.HANDOVER_ACCEPTED.value) {
            return HandoverAcceptedMessage;
        }

        return AssistentMessage;
    }
</script>

<style scoped lang="scss">
    .chat-content {
        color: #2e364d;
        padding: 1rem;
        overflow-y: scroll;
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }
</style>
